import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { message, Modal } from "antd";
import { Alert } from "components/elements";
import { getRemainingLeaveWithIn } from "features/home/services/homeApi";
import useOptions from "features/masterData/hooks/useOptions";
import { requestIdCard } from "utils/formatter/formatCommon";
import { dataURLtoFile, generateRandomString } from "utils/helper";
import { NATIONALITY } from "../schemas/employeeCreateSchema";
import * as services from "../services/employeeApi";
import { setCurrentEmploymentHome, setEmpRemainingLeave, setIsLoading } from "../slices/employeeSlice";
import useEmployeeEdit from "./useEmployeeEdit";


const FORMAT_DATE = "YYYY-MM-DD";


const toInitialValues = (initialValues = {}) => {
  let dateOfBirth = initialValues?.dateOfBirth
    ? dayjs(initialValues?.dateOfBirth)
    : null;
  let workPermitIssueDate = initialValues?.workPermitIssueDate
    ? dayjs(initialValues?.workPermitIssueDate)
    : null;
  let workPermitExpireDate = initialValues?.workPermitExpireDate
    ? dayjs(initialValues?.workPermitExpireDate)
    : null;
  let passportIssueDate = initialValues?.passportIssueDate
    ? dayjs(initialValues?.passportIssueDate)
    : null;
  let passportExpireDate = initialValues?.passportExpireDate
    ? dayjs(initialValues?.passportExpireDate)
    : null;
  let visaIssueDate = initialValues?.visaIssueDate
    ? dayjs(initialValues?.visaIssueDate)
    : null;
  let visaExpireDate = initialValues?.visaExpireDate
    ? dayjs(initialValues?.visaExpireDate)
    : null;
  const date2 = dayjs();
  let years = date2.diff(dateOfBirth, 'year');

  return {
    ...initialValues,
    age: years,
    titleId: initialValues?.title?.id,
    raceId: initialValues?.race?.id,
    genderId: initialValues?.gender?.id,
    dateOfBirth: dateOfBirth,
    nationalityId: initialValues?.nationality?.id,
    religionId: initialValues?.religion?.id,
    maritalStatusId: initialValues?.maritalStatus?.id,
    militaryStatusId: initialValues?.militaryStatus?.id,
    genderCode: initialValues?.gender?.nameEn.toUpperCase(),
    workPermitNo: initialValues?.workPermitNo,
    workPermitIssueDate: workPermitIssueDate,
    workPermitExpireDate: workPermitExpireDate,
    passportIssueCountryId: initialValues?.passportIssueCountry?.id,
    passportIssueDate: passportIssueDate,
    passportExpireDate: passportExpireDate,
    visaTypeId: initialValues?.visaType?.id,
    visaIssueCountryId: initialValues?.visaIssueCountry?.id,
    visaIssueDate: visaIssueDate,
    visaExpireDate: visaExpireDate,
    bloodGroupId: initialValues?.bloodGroup?.id,
    employeeImage: initialValues?.employeeImage
      ? dataURLtoFile(
        "data:image/png;base64, " + initialValues?.employeeImage,
        "example.png"
      )
      : null,

  };
};

const toParams = (values = {}) => {
  const modifiedValues = {
    ...values,
    dateOfBirth: values.dateOfBirth
      ? values.dateOfBirth.format(FORMAT_DATE)
      : null,

    employeeNationality:
      values.employeeNationality === NATIONALITY.DOMESTIC ? "D" : "F",

    workPermitIssueDate: values.workPermitIssueDate
      ? values.workPermitIssueDate.format(FORMAT_DATE)
      : null,

    workPermitExpireDate: values.workPermitExpireDate
      ? values.workPermitExpireDate.format(FORMAT_DATE)
      : null,
    passportIssueDate: values.passportIssueDate ? values.passportIssueDate.format(FORMAT_DATE) : null,
    passportExpireDate: values.passportExpireDate ? values.passportExpireDate.format(FORMAT_DATE) : null,
    visaIssueDate: values.visaIssueDate ? values.visaIssueDate.format(FORMAT_DATE) : null,
    visaExpireDate: values.visaExpireDate ? values.visaExpireDate.format(FORMAT_DATE) : null,
  };

  return modifiedValues;
};

function useEmployeeGeneralInfo(nev = true) {
  const { i18n } = useTranslation();
  let { id } = useParams();
  let employeeId = id;
  const [lang, setLang] = useState("en");
  const dispatch = useDispatch();
  const currentEmployee = useSelector(
    (state) => state.employee.currentEmployee
  );
  const currentEmployeeOnEdit = useSelector(
    (state) => state.employee.currentEmployeeOnEdit
  );
  const currentCompany = useSelector((state) => state.company.currentCompany);

  const [isVisible, setIsVisible] = useState(false);

  const { getEmployee, getEmployeeOnEdit, isLoading } = useEmployeeEdit(nev)

  const leaveType = [
    {
      name: "Business Leave",
    },
    {
      name: "Annual Leave",
    },
    {
      name: "Sick Leave",
    },
    {
      name: "Business Leave (without pay)",
    },
  ]

  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const getEmploymentInfoCallback = useCallback(
    async () => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getEmploymentInformation({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: employeeId,
          visible: isVisible ? isVisible : false
        });
        dispatch(setCurrentEmploymentHome(response.data || {}));
      } catch (error) {
        Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, currentCompany, employeeId]
  );

  const getDataRemaining = useCallback(
    async (params) => {
      try {
        dispatch(setIsLoading(true));
        const res = await getRemainingLeaveWithIn({
          // companyId: params.currentCompany,
          // employeeId: params.currentEmployee,
          ...params,
          listName: leaveType.map(o => o.name)
        })
        dispatch(setEmpRemainingLeave(res.data))
      } catch (error) {
        Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [],
  )
  useEffect(() => {

    if (!nev) {
      getEmploymentInfoCallback()
      getDataRemaining()
    }
    getDataRemaining({ companyId: currentCompany, employeeId: employeeId })
  }, [currentCompany, employeeId, nev, getDataRemaining, getEmploymentInfoCallback])

  const {
    titleNameOptions = [],
    titleNameEnOptions = [],
    gender = [],
    raceOptions = [],
    nationalityOptions = [],
    religionOptions = [],
    maritalStatusOptions = [],
    militaryStatusOptions = [],
    countryOptions = [],
    visaTypeOptions = [],
    bloodGroupOptions = [],
    getTitleOptions,
    getTitleEnOptions,
    getGender,
    getRaceOptions,
    getNationalityOptions,
    getReligionOptions,
    getMaritalStatusOptions,
    getMilitaryStatusOptions,
    getCountryOptions,
    getVisaTypeOptions,
    getBloodGroupOptions,
  } = useOptions();
  const [isEditing, setIsEditing] = useState(false);
  const [generalInfo, setGeneralInfo] = useState({});
  const { t } = useTranslation();

  const initialValues = useMemo(() => {
    let formInitialValues;
    if (isVisible) {
      formInitialValues = toInitialValues(currentEmployeeOnEdit);
    } else {
      formInitialValues = toInitialValues(currentEmployee);
    }
    setGeneralInfo(formInitialValues)
    return formInitialValues;
  }, [currentEmployee, currentEmployeeOnEdit, isVisible]);

  const onEdit = useCallback(() => {
    getEmployeeOnEdit({ employeeId, visible: true, edit: true })
    handleVisableAndEdit(true);
  }, []);

  const handleToggleVisibility = () => {
    setIsVisible(prev => {
      const updatedVisible = !prev;
      getEmployeeOnEdit({ employeeId, visible: updatedVisible, edit: false });
      return updatedVisible;
    });
  };

  const handleVisableAndEdit = (flag) => {
    setIsEditing(flag)
    setIsVisible(flag)
  }

  const onCancel = useCallback(() => {
    handleVisableAndEdit(false);
  }, []);

  const updateGeneralInformation = useCallback(
    async (values) => {
      // console.log("🚀 ~ values:", values)
      try {
        dispatch(setIsLoading(true));
        const response = await services.updateGeneralInformation(
          toParams({
            ...values,
            requestId: generateRandomString(),
            companyId: currentCompany,
            employeeId: employeeId,
            visible: values.visible,
            identificationNo: requestIdCard(values.employeeNationality === 'F' ? values.taxId : values.identificationNo || '-'),
            identificationNoOrTaxId: requestIdCard(values.employeeNationality === 'F' ? values.taxId : values.identificationNo || '-')
          })
        );
        // console.log('respo', response);
        message.success(t("common.success"))
        getEmployee({ employeeId, visible: false, edit: false })
        handleVisableAndEdit(false);
      } catch (error) {
        console.log("error", error);
        function contentDetail(params) {
          return error?.data?.status?.details?.map?.(o => {
            return <div>{o?.key} {o?.value}</div>
          })
        }
        Modal.error({ content: (contentDetail()), okText: t("confirm") })
        // Alert({ type: "error", resultObject: error, noti: true });
        // alert({ message: error?.data?.status?.description });

      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, getEmployee, dispatch]
  );

  const onSubmit = useCallback(
    (values) => {
      console.log(" save", values)
      Modal.confirm({
        title: t('generalInfo.confirmation'),
        content: (
          <>
            <p>{t('generaInfo.are-you-sure-confirm-to-edit')}</p>
          </>
        ),
        onOk() {
          updateGeneralInformation(values);
        },
        okText: t('confirm'),
        cancelText: t('generalInfo.cancel'),
      });
    },
    [updateGeneralInformation]
  );

  const updateImageInformationProfile = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.updateImageInformationProfile(
          toParams({
            requestId: generateRandomString(),
            companyId: currentCompany,
            employeeId: employeeId,
            employeeImage: values?.employeeImage
          })
        );
        message.success(t("common.success"))
        getEmployee({ employeeId, visible: false, edit: false });
        handleVisableAndEdit(false);
        setIsModalOpen(false);
      } catch (error) {
        message.success(t("common.success"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, employeeId]
  );

  const onImageSubmit = useCallback(
    (values) => {
      Modal.confirm({
        title: t('generalInfo.confirmation'),
        content: (
          <>
            <p>{t('generaInfo.are-you-sure-confirm-to-edit')}</p>
          </>
        ),
        onOk() {
          updateImageInformationProfile(values);
        },
        okText: t('confirm'),
        cancelText: t('generalInfo.cancel'),
      });
    },
    [t, updateImageInformationProfile]
  );

  useEffect(() => {
    getTitleOptions();
    getTitleEnOptions();
    getGender();
    getRaceOptions();
    getNationalityOptions();
    getReligionOptions();
    getMaritalStatusOptions();
    getMilitaryStatusOptions();
    getBloodGroupOptions();
    getCountryOptions();
    getVisaTypeOptions();

  }, []);

  useEffect(() => {
    setLang(i18n.language);
  }, [i18n.language, setLang]);

  return {
    isEditing,
    isModalOpen,
    raceOptions,
    genderOptions: gender,
    initialValues,
    religionOptions,
    titleNameOptions,
    titleNameEnOptions,
    bloodGroupOptions,
    nationalityOptions,
    maritalStatusOptions,
    militaryStatusOptions,
    countryOptions,
    visaTypeOptions,
    generalInfo: generalInfo,
    onEdit,
    onSubmit,
    onCancel,
    showModal,
    handleCancel,
    handleOk,
    onImageSubmit,
    setIsVisible,
    getEmployee,
    handleToggleVisibility,
    isLoading,
    isVisible,
  };
}

export default useEmployeeGeneralInfo;
